import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { paginate, searchMovies, sortMovies } from "@/utils/catalog";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import usePageMeta from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const page = Number(searchParams.get("page") || "1") || 1;
  const [input, setInput] = useState(keyword);

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 电影天堂` : "搜索电影 - 电影天堂",
    description: keyword
      ? `电影天堂中与“${keyword}”相关的电影搜索结果，快速找到你想看的影片。`
      : "在电影天堂中搜索最新电影与影视资源。",
    keywords: `电影天堂,电影搜索,${keyword}`,
  });

  const results = keyword ? sortMovies(searchMovies(movies, keyword), "hot") : [];
  const { items, totalPages, currentPage } = paginate(results, page, PAGE_SIZE);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    setSearchParams(params);
    window.scrollTo({ top: 0 });
  };

  const updatePage = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete("page");
    else params.set("page", String(next));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hotRecommend = sortMovies(movies, "hot").slice(0, 6);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-foreground-400" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-100">搜索结果</span>
          </nav>

          <h1 className="text-2xl font-bold text-foreground-50 md:text-3xl">搜索电影</h1>

          <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center rounded-md border border-background-300/70 bg-background-100 px-4 py-2.5 focus-within:border-accent-500/70">
              <i className="ri-search-line text-foreground-400" />
              <input
                type="search"
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入电影名称、导演或演员关键词…"
                className="ml-3 w-full bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-accent-500 px-8 py-2.5 text-sm font-medium text-background-950 transition hover:bg-accent-400"
            >
              搜索
            </button>
          </form>

          {keyword ? (
            <p className="mt-6 text-sm text-foreground-400">
              关键词 <span className="text-accent-500">“{keyword}”</span> 共找到{" "}
              <span className="text-foreground-50">{results.length}</span> 部相关影片
            </p>
          ) : (
            <p className="mt-6 text-sm text-foreground-400">请输入关键词开始搜索</p>
          )}

          {keyword && results.length === 0 ? (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg bg-background-100 py-16 ring-1 ring-background-200/70">
              <i className="ri-search-eye-line text-5xl text-foreground-500" />
              <p className="mt-4 text-lg text-foreground-100">未找到与“{keyword}”相关的影片</p>
              <p className="mt-2 text-sm text-foreground-400">换个关键词试试，或看看下面的热门推荐</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/"
                  className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-950 transition hover:bg-accent-400"
                >
                  返回首页
                </Link>
                <Link
                  to="/category/action"
                  className="whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-100 transition hover:border-accent-500/70 hover:text-accent-400"
                >
                  浏览最新电影
                </Link>
              </div>

              <div className="mt-10 w-full max-w-[1000px] px-4">
                <h2 className="mb-4 text-left text-base font-medium text-foreground-50">热门推荐</h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {hotRecommend.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                {items.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showSynopsis />
                ))}
              </div>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={updatePage} />
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}